// API Service - Real API calls to backend
// Replaces mockData.ts for production use

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://quiz.aquaticism.uno/api';

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'teacher' | 'student';
  createdAt: string;
}

export interface Course {
  id: string;
  name: string;
  description: string;
  teacherIds: string[];
  createdAt: string;
}

export interface Question {
  id: string;
  type: 'mcq' | 'short-answer';
  question: string;
  options?: string[];
  correctAnswer?: {
    answer: string;
    explanation: string;
  };
  points: number;
}

export interface Quiz {
  id: string;
  title: string;
  courseId: string;
  teacherId: string;
  description: string;
  timeLimit: number;
  questions: Question[];
  createdAt: string;
}

export interface Submission {
  id: string;
  quizId: string;
  studentId: string;
  answers: { [questionId: string]: string };
  score: number;
  totalPoints: number;
  timeSpent: number;
  completedAt: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// API Error class
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Helper function for API requests
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  // Handle 204 No Content or 201 Created with no body
  if (response.status === 204 || response.status === 201) {
    // Check if there's actually content to parse
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }
    return JSON.parse(text);
  }

  return response.json();
}

// ============ Auth API ============

export async function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function register(
  email: string,
  password: string,
  name: string,
  role: 'admin' | 'teacher' | 'student'
): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name, role }),
  });
}

export async function getCurrentUser(): Promise<User> {
  return request<User>('/auth/me');
}

// ============ Users API (Admin) ============

export async function getAllUsers(): Promise<User[]> {
  return request<User[]>('/users');
}

export async function getUserById(id: string): Promise<User> {
  return request<User>(`/users/${id}`);
}

export async function createUser(userData: {
  email: string;
  password: string;
  name: string;
  role: 'admin' | 'teacher' | 'student';
}): Promise<User> {
  return request<User>('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

export async function updateUser(id: string, updates: Partial<User>): Promise<User> {
  return request<User>(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteUser(id: string): Promise<void> {
  return request<void>(`/users/${id}`, {
    method: 'DELETE',
  });
}

// ============ Courses API ============

export async function getAllCourses(all: boolean = false): Promise<Course[]> {
  return request<Course[]>(`/courses${all ? '?all=true' : ''}`);
}

export async function getCourseById(id: string): Promise<Course> {
  return request<Course>(`/courses/${id}`);
}

export async function getCoursesByTeacher(teacherId: string): Promise<Course[]> {
  const courses = await getAllCourses();
  return courses.filter(c => c.teacherIds?.includes(teacherId));
}

export async function createCourse(courseData: {
  name: string;
  description: string;
  teacherId: string;
}): Promise<Course> {
  return request<Course>('/courses', {
    method: 'POST',
    body: JSON.stringify(courseData),
  });
}

export async function updateCourse(id: string, updates: Partial<Course>): Promise<Course> {
  return request<Course>(`/courses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteCourse(id: string): Promise<void> {
  return request<void>(`/courses/${id}`, {
    method: 'DELETE',
  });
}

// Course-Teacher management
export async function addTeacherToCourse(courseId: string, teacherId: string): Promise<void> {
  return request<void>(`/courses/${courseId}/teachers`, {
    method: 'POST',
    body: JSON.stringify({ teacherId }),
  });
}

export async function removeTeacherFromCourse(courseId: string, teacherId: string): Promise<void> {
  return request<void>(`/courses/${courseId}/teachers/${teacherId}`, {
    method: 'DELETE',
  });
}

export async function getCourseTeachers(courseId: string): Promise<string[]> {
  return request<string[]>(`/courses/${courseId}/teachers`);
}

// ============ Quizzes API ============

export async function getAllQuizzes(): Promise<Quiz[]> {
  return request<Quiz[]>('/quizzes');
}

export async function getQuizById(id: string): Promise<Quiz> {
  return request<Quiz>(`/quizzes/${id}`);
}

export async function getQuizzesByCourse(courseId: string): Promise<Quiz[]> {
  return request<Quiz[]>(`/quizzes?courseId=${courseId}`);
}

export async function getQuizzesByTeacher(teacherId: string): Promise<Quiz[]> {
  const quizzes = await getAllQuizzes();
  return quizzes.filter(q => q.teacherId === teacherId);
}

export async function createQuiz(quizData: Omit<Quiz, 'id' | 'createdAt' | 'teacherId'>): Promise<Quiz> {
  return request<Quiz>('/quizzes', {
    method: 'POST',
    body: JSON.stringify(quizData),
  });
}

export async function updateQuiz(id: string, updates: Partial<Quiz>): Promise<Quiz> {
  return request<Quiz>(`/quizzes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteQuiz(id: string): Promise<void> {
  return request<void>(`/quizzes/${id}`, {
    method: 'DELETE',
  });
}

// ============ Submissions API ============

export async function getMySubmissions(): Promise<Submission[]> {
  return request<Submission[]>('/submissions');
}

export async function getSubmissionByQuiz(quizId: string): Promise<Submission | null> {
  try {
    return await request<Submission>(`/submissions/quiz/${quizId}`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getSubmissionsByQuiz(quizId: string): Promise<Submission[]> {
  return request<Submission[]>(`/quizzes/${quizId}/submissions`);
}

export async function submitQuiz(data: {
  quizId: string;
  answers: { [questionId: string]: string };
  timeSpent: number;
}): Promise<Submission> {
  return request<Submission>('/submissions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Alias for backward compatibility
export async function createAttempt(attemptData: {
  quizId: string;
  studentId: string;
  answers: { [questionId: string]: string };
  score: number;
  totalPoints: number;
  timeSpent: number;
  completedAt: string;
}): Promise<Submission> {
  return submitQuiz({
    quizId: attemptData.quizId,
    answers: attemptData.answers,
    timeSpent: attemptData.timeSpent,
  });
}

export async function getAttemptsByStudent(studentId: string): Promise<Submission[]> {
  return getMySubmissions();
}

export async function getAttemptsByQuiz(quizId: string): Promise<Submission[]> {
  return getSubmissionsByQuiz(quizId);
}

export async function getAllAttempts(): Promise<Submission[]> {
  return getMySubmissions();
}

// ============ Analytics API ============

export interface AdminAnalytics {
  totalUsers: number;
  totalCourses: number;
  totalQuizzes: number;
  totalSubmissions: number;
}

export interface TeacherAnalytics {
  totalCourses: number;
  totalQuizzes: number;
  totalSubmissions: number;
  averageScore: number;
}

export interface StudentAnalytics {
  quizzesTaken: number;
  averageScore: number;
  totalScore: number;
  totalPossible: number;
}

export interface QuizAnalytics {
  totalAttempts: number;
  averageScore: number;
  highestScore: number;
  lowestScore: number;
  totalPossible: number;
  attempts: (Submission & { studentName: string })[];
}

export async function getAdminAnalytics(): Promise<AdminAnalytics> {
  return request<AdminAnalytics>('/analytics/admin');
}

export async function getTeacherAnalytics(): Promise<TeacherAnalytics> {
  return request<TeacherAnalytics>('/analytics/teacher');
}

export async function getStudentAnalytics(): Promise<StudentAnalytics> {
  return request<StudentAnalytics>('/analytics/student');
}

export interface DifficultyData {
  name: string;
  correct: number;
  total: number;
  percentage: number;
}

export interface StudentDifficultyAnalytics {
  difficultyData: DifficultyData[];
}

export async function getStudentDifficultyAnalytics(courseId?: string): Promise<StudentDifficultyAnalytics> {
  const params = courseId && courseId !== 'all' ? `?courseId=${courseId}` : '';
  return request<StudentDifficultyAnalytics>(`/analytics/student/difficulty${params}`);
}

export async function getQuizAnalytics(quizId: string): Promise<QuizAnalytics> {
  return request<QuizAnalytics>(`/quizzes/${quizId}/analytics`);
}

// ============ Teacher Students API ============

export interface TeacherStudent {
  id: string;
  name: string;
  email: string;
  quizzesTaken: number;
  averageScore: number;
}

export async function getTeacherStudents(): Promise<TeacherStudent[]> {
  return request<TeacherStudent[]>('/analytics/teacher/students');
}

// ============ Profile API ============

export async function updateProfile(updates: { name?: string; email?: string }): Promise<User> {
  return request<User>('/users/me', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
  return request<void>('/users/me/password', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
